/**
 * Index eligibility engine.
 *
 * This is the piece that makes a national URL architecture safe to ship before
 * national inventory exists. Every `/locations/*`, `/services/*` and
 * `/stylists/:slug` URL RESOLVES (the route always renders something useful),
 * but only URLs backed by enough real, live marketplace inventory are allowed
 * to be `index,follow` and to appear in the sitemap.
 *
 * Everything else is `noindex,follow` — crawlers can still traverse the page's
 * links to reach eligible pages, but the thin page itself never enters the
 * index. As real inventory grows, pages cross the threshold and become
 * indexable automatically on the next sitemap/prerender build. No thousands of
 * empty pages are published up front.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

/**
 * Why 3?
 *
 * A location/service landing page earns its place in the index only if it
 * answers "who can do this for me near here?" better than the generic browse
 * page. With 1 stylist the page is a worse duplicate of that stylist's own
 * profile; with 2 there is no real choice to compare. 3 is the smallest count
 * that produces a genuine shortlist, and it is also the smallest count where
 * templated copy ("N professionals", "$X-$Y typical range") is derived from
 * more than a single data point. It is a single constant so it can be raised
 * once real search-quality data exists post-launch.
 */
const MIN_STYLISTS_FOR_CITY_INDEX = 3;

/** City+service is the most specific (and thinnest) combination — same bar. */
const MIN_STYLISTS_FOR_CITY_SERVICE_INDEX = 3;

/**
 * A state page aggregates cities, so it needs both breadth (at least one city
 * that is itself indexable) and depth (enough total supply to be worth a hub).
 */
const MIN_STYLISTS_FOR_STATE_INDEX = 5;
const MIN_INDEXABLE_CITIES_FOR_STATE_INDEX = 1;

/** A national service hub links out to city pages; it needs real national supply. */
const MIN_STYLISTS_FOR_SERVICE_INDEX = 5;

const ROBOTS_INDEX = 'index, follow';
const ROBOTS_NOINDEX = 'noindex, follow';

/**
 * @typedef {Object} EligibilityResult
 * @property {boolean} indexable
 * @property {string[]} reasons  machine-readable reasons when not indexable
 * @property {string}  robots    the meta robots content string to emit
 */

/**
 * @param {boolean} indexable
 * @param {string[]} reasons
 * @returns {EligibilityResult}
 */
function result(indexable, reasons) {
  return {
    indexable,
    reasons: reasons || [],
    robots: indexable ? ROBOTS_INDEX : ROBOTS_NOINDEX,
  };
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * A public stylist profile is indexable only when it is a real, approved,
 * live listing with enough substance to stand as its own page.
 *
 * @param {Object | null | undefined} stylist public profile projection
 * @returns {EligibilityResult}
 */
function evaluateStylistProfile(stylist) {
  const reasons = [];
  if (!stylist) return result(false, ['missing_profile']);

  if (!stylist.seoSlug) reasons.push('missing_seo_slug');
  if (stylist.approvalStatus && stylist.approvalStatus !== 'approved') {
    reasons.push('not_approved');
  }
  if (stylist.emailVerified !== true) reasons.push('email_unverified');
  if (stylist.isDeleted === true || stylist.deletionRequested === true) {
    reasons.push('deleted');
  }
  if (!stylist.city || !stylist.state) reasons.push('missing_location');

  const hasBio = Boolean(
    (stylist.shortBio && String(stylist.shortBio).trim()) ||
      (stylist.fullBio && String(stylist.fullBio).trim())
  );
  if (!hasBio) reasons.push('missing_bio');

  if (toCount(stylist.serviceCount) < 1) reasons.push('no_published_services');

  return result(reasons.length === 0, reasons);
}

/**
 * @param {{ stylistCount?: number, city?: string, state?: string }} city
 * @returns {EligibilityResult}
 */
function evaluateCity(city) {
  const reasons = [];
  if (!city) return result(false, ['missing_city']);
  if (!city.city || !city.state) reasons.push('missing_location');
  if (toCount(city.stylistCount) < MIN_STYLISTS_FOR_CITY_INDEX) {
    reasons.push('below_min_stylists');
  }
  return result(reasons.length === 0, reasons);
}

/**
 * @param {{ stylistCount?: number, city?: string, state?: string, serviceCategory?: string }} combo
 * @returns {EligibilityResult}
 */
function evaluateCityService(combo) {
  const reasons = [];
  if (!combo) return result(false, ['missing_combination']);
  if (!combo.city || !combo.state) reasons.push('missing_location');
  if (!combo.serviceCategory) reasons.push('missing_service_category');
  if (toCount(combo.stylistCount) < MIN_STYLISTS_FOR_CITY_SERVICE_INDEX) {
    reasons.push('below_min_stylists');
  }
  return result(reasons.length === 0, reasons);
}

/**
 * @param {{ stylistCount?: number, state?: string, cities?: Array<{ stylistCount?: number }> }} state
 * @returns {EligibilityResult}
 */
function evaluateState(state) {
  const reasons = [];
  if (!state) return result(false, ['missing_state']);
  if (!state.state) reasons.push('missing_location');
  if (toCount(state.stylistCount) < MIN_STYLISTS_FOR_STATE_INDEX) {
    reasons.push('below_min_stylists');
  }
  const cities = Array.isArray(state.cities) ? state.cities : [];
  const indexableCities = cities.filter((c) => evaluateCity(c).indexable).length;
  if (indexableCities < MIN_INDEXABLE_CITIES_FOR_STATE_INDEX) {
    reasons.push('no_indexable_cities');
  }
  return result(reasons.length === 0, reasons);
}

/**
 * @param {{ stylistCount?: number, serviceCategory?: string }} service
 * @returns {EligibilityResult}
 */
function evaluateServiceHub(service) {
  const reasons = [];
  if (!service) return result(false, ['missing_service']);
  if (!service.serviceCategory) reasons.push('missing_service_category');
  if (toCount(service.stylistCount) < MIN_STYLISTS_FOR_SERVICE_INDEX) {
    reasons.push('below_min_stylists');
  }
  return result(reasons.length === 0, reasons);
}

module.exports = {
  MIN_STYLISTS_FOR_CITY_INDEX,
  MIN_STYLISTS_FOR_CITY_SERVICE_INDEX,
  MIN_STYLISTS_FOR_STATE_INDEX,
  MIN_INDEXABLE_CITIES_FOR_STATE_INDEX,
  MIN_STYLISTS_FOR_SERVICE_INDEX,
  ROBOTS_INDEX,
  ROBOTS_NOINDEX,
  evaluateStylistProfile,
  evaluateCity,
  evaluateCityService,
  evaluateState,
  evaluateServiceHub,
};
